import { parse } from "query-string";
import { call, put, select, takeLatest } from "redux-saga/effects";

import { analyticsStoreEvent } from "@/mk2/actions";
import { homepageUrl } from "@/mk2/apps/home/urls";
import {
    SOCIAL_COMPLETE_SIGNUP_SUCCESS,
    SOCIAL_COMPLETE_SIGNUP_TRIGGER,
    SOCIAL_LINK_PASSWORD_FAILURE,
    SOCIAL_LINK_PASSWORD_SUCCESS,
    SOCIAL_LINK_PASSWORD_TRIGGER,
    SOCIAL_LOGIN_FAILURE,
    SOCIAL_LOGIN_NEEDS_COMPLETION,
    SOCIAL_LOGIN_NEEDS_PASSWORD,
    SOCIAL_LOGIN_TRIGGER,
    SOCIAL_SKIP_PROFILE_UPDATE,
    SOCIAL_UPDATE_PROFILE_FAILURE,
    SOCIAL_UPDATE_PROFILE_SUCCESS,
    SOCIAL_UPDATE_PROFILE_TRIGGER,
    SocialCompleteSignupTriggerAction,
    SocialLinkPasswordTriggerAction,
    SocialLoginTriggerAction,
    SocialSkipProfileUpdateAction,
    SocialUpdateProfileTriggerAction,
} from "@/mk2/apps/users/containers/SocialAuth/SocialAuth.actions";
import { getFacebookUserInfo, signInWithFacebook } from "@/mk2/apps/users/lib/socialAuth/facebook";
import { signInWithGoogle } from "@/mk2/apps/users/lib/socialAuth/google";
import { redirectToMojeID } from "@/mk2/apps/users/lib/socialAuth/mojeid";
import {
    AllAuthResponseBody,
    AllAuthXHRError,
    SocialProvider,
} from "@/mk2/apps/users/lib/socialAuth/types";
import { socialAuthCallbackUrl, socialCompleteSignupUrl } from "@/mk2/apps/users/urls";
import {
    gotoUrlAfterLoginOrSignup,
    gtmEvent,
    storeLastKnownUsernameCookie,
} from "@/mk2/apps/users/utils";
import { XHRAction } from "@/mk2/helpers/api";
import { showErrorToastAction } from "@/mk2/helpers/toasts";
import { HistoryAction, pushInPWA } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    SOCIAL_AUTH_ERROR_FACEBOOK_EMAIL_REQUIRED,
    SOCIAL_AUTH_ERROR_INVALID_PASSWORD,
    SOCIAL_AUTH_ERROR_SIGNUP_FAILED,
    SOCIAL_AUTH_ERROR_TOO_MANY_ATTEMPTS,
    SOCIAL_AUTH_ERROR_USERNAME_TAKEN,
    SOCIAL_AUTH_GOOGLE_ACCOUNT_LINKED_TO_EXISTING_PROFILE_username,
    SOCIAL_AUTH_LOGGED_IN_AS_username,
} from "@/mk/autogenerated/translation";
import { FACEBOOK_SOCIAL_APP_ID } from "@/mk/settings";

// Google client ID
// TODO: move to settings/initialState once social login goes to production
const GOOGLE_CLIENT_ID = "171359808546-n0tik01amenb1mfn0pq1340p8eocauvt.apps.googleusercontent.com";

function getRedirectUri(): string {
    return window.location.origin + socialAuthCallbackUrl;
}

function decodeJwtPayload(token: string): Record<string, string> {
    try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return {};
    }
}

function* socialLogin({
    provider,
    location,
    xhr,
    routeNavigate,
}: SocialLoginTriggerAction & XHRAction & HistoryAction) {
    try {
        gtmEvent("event_social_login_triggered", { social: provider });

        // Step 1: Get token from provider OAuth
        const redirectUri = getRedirectUri();
        let idToken: string | undefined;
        let accessToken: string | undefined;
        let userEmail = "";
        let userName = "";

        if (provider === "google") {
            idToken = (yield call(signInWithGoogle, GOOGLE_CLIENT_ID, redirectUri)) as string;
            const jwtPayload = decodeJwtPayload(idToken);
            userEmail = jwtPayload.email || "";
            userName = jwtPayload.name || "";
            if (!userEmail) {
                throw new Error(
                    "Google account has no email address. Please use a different account.",
                );
            }
        } else if (provider === "facebook") {
            accessToken = yield call(signInWithFacebook);
            const fbUser: { email?: string; name?: string } = yield call(
                getFacebookUserInfo,
                accessToken,
            );
            // TODO: remove debug event after FB login is verified
            yield put(
                analyticsStoreEvent("debug_fb_graph_response", {
                    response: JSON.stringify(fbUser),
                }),
            );
            userEmail = fbUser.email || "";
            userName = fbUser.name || "";
            if (!userEmail) {
                throw new Error(SOCIAL_AUTH_ERROR_FACEBOOK_EMAIL_REQUIRED);
            }
        } else if (provider === "mojeid") {
            // mojeID uses a full-page redirect (authorization code flow), not a popup or SDK.
            // We navigate directly to the allauth redirect endpoint; allauth sets state/nonce
            // in the session and redirects the browser to mojeid.cz. After authentication,
            // mojeid.cz redirects back to /_allauth/.../provider/callback which allauth handles.
            const nextParam = (parse(location.search).next as string) || "";
            const callbackUrl = nextParam || homepageUrl;
            yield call(() => {
                redirectToMojeID(callbackUrl);
            });
            return; // page navigates away
        } else {
            throw new Error(`Provider ${provider} not yet implemented`);
        }

        // Step 2: Send token to allauth provider/token endpoint
        const tokenPayload: Record<string, string> = {};
        if (idToken) {
            tokenPayload.id_token = idToken;
            tokenPayload.client_id = GOOGLE_CLIENT_ID;
        }
        if (accessToken) {
            tokenPayload.access_token = accessToken;
            tokenPayload.client_id = FACEBOOK_SOCIAL_APP_ID;
        }
        const body = {
            provider,
            token: tokenPayload,
            process: "login",
        };

        let responseBody: AllAuthResponseBody;
        try {
            const response: { body: AllAuthResponseBody } = yield call(() =>
                xhr.post("/_allauth/browser/v1/auth/provider/token", body),
            );
            responseBody = response.body;
        } catch (xhrError: unknown) {
            const err = xhrError as AllAuthXHRError;
            // allauth returns 401 for "needs signup" — XHR treats it as error
            if (err?.status === 401 && err?.response?.body) {
                responseBody = err.response.body;
            } else {
                throw xhrError;
            }
        }

        // Step 3: Handle response
        if (responseBody.status === 200 && responseBody.data?.user) {
            // Authenticated — existing user or auto-linked
            const user = responseBody.data.user;
            const username = user.username || user.display || user.email;

            storeLastKnownUsernameCookie(username);
            gtmEvent("event_social_login_success", { social: provider });

            // Store toast message for after reload
            // (hard reload needed to get fresh initialState with authenticated user)
            try {
                const toastMessage = interpolate(SOCIAL_AUTH_LOGGED_IN_AS_username, { username });
                sessionStorage.setItem("socialauth_toast", toastMessage);
            } catch (e) {
                // sessionStorage may be unavailable
            }

            // Hard reload for fresh initialState — preserve ?next= if present
            window.location.href = gotoUrlAfterLoginOrSignup("", location, homepageUrl, true);
        } else if (
            responseBody.status === 401 ||
            responseBody.data?.flows?.some((f) => f.id === "provider_signup")
        ) {
            // Check if this is a password-needed conflict or a new signup
            let loginState: { action: string } = { action: "needs_signup" };
            try {
                const stateResp: { body: { action: string } } = yield call(() =>
                    xhr.get("/api/users/social-login-state/"),
                );
                loginState = stateResp.body;
            } catch {
                // Default to signup if state check fails
            }

            // Forward ?next= so signup/complete can redirect there after completion
            const nextParam = (parse(location.search).next as string) || "";
            const completeUrl = nextParam
                ? `${socialCompleteSignupUrl}?next=${encodeURIComponent(nextParam)}`
                : socialCompleteSignupUrl;

            if (loginState.action === "needs_password") {
                // Email conflict — existing user has a password, needs verification
                yield put({
                    type: SOCIAL_LOGIN_NEEDS_PASSWORD,
                    provider,
                    email: userEmail,
                });
                yield call(pushInPWA, routeNavigate, completeUrl);
            } else {
                // New user — normal signup flow
                let suggestedUsername: string | undefined;
                if (userEmail) {
                    try {
                        const suggestResp: { body: { suggestion: string } } = yield call(() =>
                            xhr.get(
                                `/api/users/suggest-username/?email=${encodeURIComponent(userEmail)}`,
                            ),
                        );
                        suggestedUsername = suggestResp.body?.suggestion;
                    } catch {
                        // Non-critical — user can still type manually
                    }
                }

                yield put({
                    type: SOCIAL_LOGIN_NEEDS_COMPLETION,
                    provider,
                    prefill: {
                        email: userEmail,
                        fullName: userName,
                        suggestedUsername,
                    },
                });
                yield call(pushInPWA, routeNavigate, completeUrl);
            }
        } else {
            throw new Error("Unexpected response from social login");
        }
    } catch (error: unknown) {
        const err = error as AllAuthXHRError;
        let errorMessage = err?.message || "Social login failed";
        if (err?.status === 403 || err?.status === 409) {
            errorMessage = SOCIAL_AUTH_ERROR_SIGNUP_FAILED;
        }
        yield put({
            type: SOCIAL_LOGIN_FAILURE,
            provider,
            error: errorMessage,
        });

        if (
            !errorMessage.includes("dismissed") &&
            !errorMessage.includes("skipped") &&
            !errorMessage.includes("cancelled")
        ) {
            yield put(showErrorToastAction(errorMessage));
        }

        gtmEvent("event_social_login_failed", { social: provider });
    }
}

function* socialCompleteSignup({
    formData,
    location,
    xhr,
    routeNavigate,
}: SocialCompleteSignupTriggerAction & XHRAction & HistoryAction) {
    try {
        const response: { body: AllAuthResponseBody } = yield call(() =>
            xhr.post("/_allauth/browser/v1/auth/provider/signup", {
                username: formData.username,
            }),
        );

        const responseBody = response.body;

        if (responseBody.data?.user || responseBody.meta?.is_authenticated) {
            const user = responseBody.data?.user;
            storeLastKnownUsernameCookie(user?.username || user?.email || "");

            const signupProvider: string = yield select(
                (state: any) => state.containers?.users?.socialAuth?.provider,
            );
            gtmEvent("event_social_signup_success", { social: signupProvider });

            // Refresh CSRF token: allauth rotates it on login, so the module-level
            // cached token is stale and would cause step 2 POST to fail with CSRF_CHANGED_ERROR.
            yield call(() => xhr.refreshApiAuth());

            yield put({
                type: SOCIAL_COMPLETE_SIGNUP_SUCCESS,
            });
        }
    } catch (error: unknown) {
        const err = error as AllAuthXHRError;

        // 409 = user already authenticated (e.g. repair from failed previous attempt)
        if (err?.status === 409 && err?.response?.body?.meta?.is_authenticated) {
            yield call(() => xhr.refreshApiAuth());
            yield put({ type: SOCIAL_COMPLETE_SIGNUP_SUCCESS });
            return;
        }

        // Extract error message from allauth response
        const allAuthErrors = err?.response?.body?.errors;
        let errorMsg = SOCIAL_AUTH_ERROR_SIGNUP_FAILED;
        if (Array.isArray(allAuthErrors) && allAuthErrors.length > 0) {
            errorMsg = allAuthErrors
                .map((e) =>
                    e.code === "username_taken" ? SOCIAL_AUTH_ERROR_USERNAME_TAKEN : e.message,
                )
                .join(". ");
        }

        yield put({
            type: "SOCIAL_COMPLETE_SIGNUP_FAILURE",
            error: errorMsg,
        });
        yield put(showErrorToastAction(errorMsg));
    }
}

function* socialUpdateProfile({
    formData,
    location,
    xhr,
}: SocialUpdateProfileTriggerAction & XHRAction) {
    try {
        const response: { body: { ok: boolean } } = yield call(() =>
            xhr.post("/api/users/update-social-profile/", formData),
        );

        if (response.body?.ok) {
            const updateProvider: string = yield select(
                (state: any) => state.containers?.users?.socialAuth?.provider,
            );
            gtmEvent("event_social_profile_updated", { social: updateProvider });

            window.location.href = gotoUrlAfterLoginOrSignup("", location, homepageUrl, true);
        }
    } catch (error: unknown) {
        const err = error as AllAuthXHRError;
        const errorMsg = err?.message || "Failed to update profile";

        yield put({
            type: SOCIAL_UPDATE_PROFILE_FAILURE,
            error: errorMsg,
        });
        yield put(showErrorToastAction(errorMsg));
    }
}

function* socialLinkWithPassword({
    password,
    location,
    xhr,
}: SocialLinkPasswordTriggerAction & XHRAction & HistoryAction) {
    try {
        const response: { body: { ok: boolean; username: string } } = yield call(() =>
            xhr.post("/api/users/social-link-with-password/", { password }),
        );

        if (response.body?.ok) {
            const username = response.body.username;
            storeLastKnownUsernameCookie(username);

            const linkProvider: string = yield select(
                (state: any) => state.containers?.users?.socialAuth?.provider,
            );
            gtmEvent("event_social_login_success", { social: linkProvider });

            try {
                sessionStorage.setItem(
                    "socialauth_toast",
                    interpolate(SOCIAL_AUTH_LOGGED_IN_AS_username, { username }),
                );
            } catch {
                // sessionStorage may be unavailable
            }

            yield put({ type: SOCIAL_LINK_PASSWORD_SUCCESS });
            window.location.href = gotoUrlAfterLoginOrSignup("", location, homepageUrl, true);
        }
    } catch (error: unknown) {
        const err = error as AllAuthXHRError;
        let errorMsg = SOCIAL_AUTH_ERROR_INVALID_PASSWORD;
        if (err?.status === 429) {
            errorMsg = SOCIAL_AUTH_ERROR_TOO_MANY_ATTEMPTS;
        } else if (err?.status === 409) {
            errorMsg = SOCIAL_AUTH_ERROR_SIGNUP_FAILED;
        }

        yield put({
            type: SOCIAL_LINK_PASSWORD_FAILURE,
            error: errorMsg,
        });
        yield put(showErrorToastAction(errorMsg));
    }
}

function* socialSkipProfileUpdate({ location }: SocialSkipProfileUpdateAction) {
    yield call(() => {
        window.location.href = gotoUrlAfterLoginOrSignup("", location, homepageUrl, true);
    });
}

export function* socialAuthSaga() {
    yield takeLatest(SOCIAL_LOGIN_TRIGGER, socialLogin);
    yield takeLatest(SOCIAL_COMPLETE_SIGNUP_TRIGGER, socialCompleteSignup);
    yield takeLatest(SOCIAL_UPDATE_PROFILE_TRIGGER, socialUpdateProfile);
    yield takeLatest(SOCIAL_SKIP_PROFILE_UPDATE, socialSkipProfileUpdate);
    yield takeLatest(SOCIAL_LINK_PASSWORD_TRIGGER, socialLinkWithPassword);
}
